import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Loader2, CheckCircle2, Cpu } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { productionMethodsApi } from '@/api/productionMethodsApi';
import type { Machine } from '@/types/api';

interface MachineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    code: string;
    name: string;
    description?: string;
    productionMethodId?: string | null;
    sortOrder?: number;
    isActive?: boolean;
  }) => Promise<void>;
  machine?: Machine | null;
}

export function MachineModal({ isOpen, onClose, onSave, machine }: MachineModalProps) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [productionMethodId, setProductionMethodId] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { data: productionMethods } = useQuery({
    queryKey: ['production-methods'],
    queryFn: () => productionMethodsApi.getAll(false),
    enabled: isOpen,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (machine) {
      setCode(machine.code);
      setName(machine.name);
      setDescription(machine.description || '');
      setProductionMethodId(machine.productionMethodId || '');
      setSortOrder(String(machine.sortOrder ?? 0));
      setIsActive(machine.isActive ?? true);
    } else {
      setCode('');
      setName('');
      setDescription('');
      setProductionMethodId('');
      setSortOrder('0');
      setIsActive(true);
    }
    setError('');
  }, [machine, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) {
      setError('Code and name are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSave({
        code: code.trim().toUpperCase().replace(/\s+/g, '_'),
        name: name.trim(),
        description: description.trim() || undefined,
        productionMethodId: productionMethodId || null,
        sortOrder: parseInt(sortOrder, 10) || 0,
        isActive,
      });
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save machine');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={!loading ? onClose : undefined} />
      <div className="relative w-full max-w-md bg-white dark:bg-charcoal-900 border border-gray-200 dark:border-charcoal-700 rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-charcoal-700">
          <div className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-amber-450" />
            <h3 className="font-display font-semibold">{machine ? 'Edit Machine' : 'Add Machine'}</h3>
          </div>
          <button type="button" onClick={onClose} disabled={loading}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <p className="text-sm text-danger-400">{error}</p>}
          <div>
            <label className="block text-sm font-medium mb-1">Code *</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="BAMBU_X1C" required className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Bambu X1C" required className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full px-3 py-2 border rounded-lg resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Linked production method</label>
            <select value={productionMethodId} onChange={(e) => setProductionMethodId(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
              <option value="">Any / general</option>
              {productionMethods?.map((m) => (
                <option key={m.id} value={m.id}>{m.icon} {m.name}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Sort order</label>
              <input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            {machine && (
              <label className="flex items-center gap-2 pt-7 cursor-pointer">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded text-amber-450" />
                <span className="text-sm">Active</span>
              </label>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1 inline" />Save</>}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
